import { useMemo, useState, useEffect } from "react";
import { calculateRentalAnalysis } from "../utils/rentalAnalysisCalculator";
import CurrencyInput from "./CurrencyInput";

export default function RentalValueAnalysis({
  property,
  prediction,
  propertyAnalysis,
  mlRent,
  agentRent,
  lowRent,
  highRent,
  discountedPrice,
  mortgageConfig,
  tenantRent,
  monthlyAssessment = 0,
}) {
  // Calculate rental value analysis using utility function
  // Now using lowRent and highRent which respect radio button selections
  const analysis = useMemo(() => {
    return calculateRentalAnalysis({
      property,
      prediction,
      propertyAnalysis,
      mlRent,
      agentRent,
      userRent: highRent,
      discountedPrice,
      lowRent,
      highRent,
      mortgageConfig,
      monthlyAssessment,
    });
  }, [property, prediction, propertyAnalysis, mlRent, agentRent, highRent, lowRent, discountedPrice, mortgageConfig, monthlyAssessment]);

  // Calculate tenant rent analysis if tenant rent is available
  const tenantAnalysis = useMemo(() => {
    if (!tenantRent) return null;
    return calculateRentalAnalysis({
      property,
      prediction,
      propertyAnalysis,
      mlRent: tenantRent,
      agentRent: tenantRent,
      userRent: tenantRent,
      discountedPrice,
      lowRent: tenantRent,
      highRent: tenantRent,
      mortgageConfig,
      monthlyAssessment,
    });
  }, [property, prediction, propertyAnalysis, tenantRent, discountedPrice, mortgageConfig, monthlyAssessment]);

  // ── What-if playground: editable purchase price / discount / rent -> live cap rate.
  // Local-only (never saved). Hooks MUST run before the early return below.
  const baseAsk = property?.asking_price || property?.price || discountedPrice || 0;
  const initialDiscountPct = (baseAsk && discountedPrice)
    ? Math.max(0, Math.round((1 - discountedPrice / baseAsk) * 1000) / 10)
    : 0;
  const [pgPrice, setPgPrice] = useState(baseAsk);
  const [pgDiscount, setPgDiscount] = useState(initialDiscountPct);
  const [pgRent, setPgRent] = useState(Math.round(highRent || mlRent || 0));
  const [pgClosing, setPgClosing] = useState(Math.round(propertyAnalysis?.net_closing_costs || 0));
  // Seed rent + closing costs once they're available (data can load after the first render).
  useEffect(() => {
    if (!pgRent && (highRent || mlRent)) setPgRent(Math.round(highRent || mlRent));
  }, [highRent, mlRent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pgClosing && propertyAnalysis?.net_closing_costs) setPgClosing(Math.round(propertyAnalysis.net_closing_costs));
  }, [propertyAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  const pgDiscountedPrice = Math.round((pgPrice || 0) * (1 - (pgDiscount || 0) / 100));
  const pgAnalysis = useMemo(() => calculateRentalAnalysis({
    property, prediction, propertyAnalysis,
    mlRent: pgRent, agentRent: pgRent, userRent: pgRent,
    lowRent: pgRent, highRent: pgRent,
    discountedPrice: pgDiscountedPrice, mortgageConfig, monthlyAssessment,
  }), [property, prediction, propertyAnalysis, pgRent, pgDiscountedPrice, mortgageConfig, monthlyAssessment]);

  // Closing costs go into the cap-rate BASIS (all-in), not the mortgage — you don't finance
  // them. Cap rate is unlevered (NOI/price), so scale by price/(price+closing). Cashflow stays
  // price-based (mortgage unchanged).
  const pgBasis = pgDiscountedPrice + (pgClosing || 0);
  const pgScale = pgBasis > 0 ? pgDiscountedPrice / pgBasis : 1;
  const pgCapPI = (pgAnalysis?.highPI?.capRate || 0) * pgScale;
  const pgCapIO = (pgAnalysis?.highIO?.capRate || 0) * pgScale;

  if (!analysis) return null;

  // Get property price for break-even down payment dollar amount calculation
  const propertyPrice = discountedPrice || property?.price || property?.asking_price || 0;

  // Styles
  const containerStyle = {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    border: "2px solid #000",
    marginBottom: "20px",
  };

  const titleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#333",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "center",
    padding: "10px 8px",
    borderBottom: "2px solid #000",
    fontWeight: "700",
    color: "#333",
    backgroundColor: "#f8f9fa",
  };

  const tdStyle = {
    textAlign: "center",
    padding: "10px 8px",
    borderBottom: "1px solid #e0e0e0",
  };

  const firstColumnStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: "600",
    backgroundColor: "#f8f9fa",
  };

  const formatCurrency = (value) => {
    // Fix negative zero display
    const normalizedValue = value === 0 ? 0 : value;
    return normalizedValue < 0 ? `-$${Math.abs(normalizedValue).toLocaleString()}` : `$${normalizedValue.toLocaleString()}`;
  };

  const formatPercent = (value) => {
    // Fix negative zero display
    const normalizedValue = value === 0 ? 0 : value;
    return `${normalizedValue.toFixed(2)}%`;
  };

  const formatBreakEvenDownPayment = (breakEvenPct, propertyPrice) => {
    if (breakEvenPct === null) return "N/A";
    // If break-even is less than 1%, show "Full Financing Possible"
    if (breakEvenPct < 1) {
      return (
        <>
          0%<br />
          Full Financing Possible
        </>
      );
    }
    const downPaymentAmount = Math.round((propertyPrice * breakEvenPct) / 100);
    return (
      <>
        {formatPercent(breakEvenPct)}<br />
        {formatCurrency(downPaymentAmount)}
      </>
    );
  };

  const getCashflowColor = (value) => {
    return value >= 0 ? "#28a745" : "#dc3545";
  };

  const pgInputStyle = {
    display: "block",
    width: "120px",
    marginTop: "3px",
    padding: "5px 7px",
    border: "1px solid #cfe2ff",
    borderRadius: "4px",
    fontSize: "13px",
  };
  const pgLabelStyle = { fontSize: "12px", color: "#333", fontWeight: 600 };

  return (
    <div style={containerStyle}>
      <div style={titleStyle}>Rental Value Analysis</div>

      {/* What-if playground — edit price / discount / rent to see cap rate live (not saved) */}
      <div style={{ background: "#f0f7ff", border: "1px solid #cfe2ff", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
        <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "10px", color: "#0d47a1" }}>
          What-if Playground <span style={{ fontWeight: 400, color: "#666", fontSize: "12px" }}>(live, not saved)</span>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={pgLabelStyle}>Purchase price
            <CurrencyInput value={pgPrice} onChange={(v) => setPgPrice(v === "" ? 0 : v)} style={pgInputStyle} />
          </label>
          <label style={pgLabelStyle}>Discount %
            <input type="number" step="0.5" value={pgDiscount} onChange={(e) => setPgDiscount(Number(e.target.value))} style={pgInputStyle} />
          </label>
          <label style={pgLabelStyle}>Monthly rent
            <CurrencyInput value={pgRent} onChange={(v) => setPgRent(v === "" ? 0 : v)} style={pgInputStyle} />
          </label>
          <label style={pgLabelStyle}>Closing costs
            <CurrencyInput value={pgClosing} onChange={(v) => setPgClosing(v === "" ? 0 : v)} style={pgInputStyle} />
          </label>
          <div style={{ marginLeft: "auto", textAlign: "right", minWidth: "240px" }}>
            <div style={{ fontSize: "11px", color: "#666" }}>
              All-in basis: {formatCurrency(pgBasis)}
              {pgClosing > 0 ? ` (${formatCurrency(pgDiscountedPrice)} + ${formatCurrency(pgClosing)} closing)` : ""}
            </div>
            <div style={{ fontSize: "14px", color: "#333", marginTop: "2px" }}>
              Cap rate — P&I <b>{formatPercent(pgCapPI)}</b> · I/O <b>{formatPercent(pgCapIO)}</b>
            </div>
            <div style={{ fontSize: "12px", marginTop: "2px" }}>
              Net cashflow (I/O): <b style={{ color: getCashflowColor(pgAnalysis.highIO.cashflow) }}>{formatCurrency(pgAnalysis.highIO.cashflow)}</b>
            </div>
          </div>
        </div>
        <button
          onClick={() => { setPgPrice(baseAsk); setPgDiscount(initialDiscountPct); setPgRent(Math.round(highRent || mlRent || 0)); setPgClosing(Math.round(propertyAnalysis?.net_closing_costs || 0)); }}
          style={{ marginTop: "10px", fontSize: "11px", padding: "3px 10px", cursor: "pointer", background: "#fff", border: "1px solid #cfe2ff", borderRadius: "4px", color: "#0d47a1" }}>
          Reset to current
        </button>
      </div>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>Scenario</th>
            <th style={thStyle}>Low Rent<br/>P&I @ {analysis.piRate}%</th>
            <th style={thStyle}>High Rent<br/>P&I @ {analysis.piRate}%</th>
            <th style={thStyle}>Low Rent<br/>I/O @ {analysis.ioRate}%</th>
            <th style={thStyle}>High Rent<br/>I/O @ {analysis.ioRate}%</th>
            {tenantAnalysis && (
              <th style={{ ...thStyle, backgroundColor: "#ffebee", color: "#c62828" }}>Current Tenant<br/>I/O @ {analysis.ioRate}%</th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={firstColumnStyle}>Rent</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.rent)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: 700, color: "#c62828" }}>{formatCurrency(tenantAnalysis.highIO.rent)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Monthly Mortgage</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.mortgage)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee" }}>{formatCurrency(tenantAnalysis.highIO.mortgage)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Monthly Carry</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.carry)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee" }}>{formatCurrency(tenantAnalysis.highIO.carry)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Net Cashflow</td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.lowPI.cashflow) }}>
              {formatCurrency(analysis.lowPI.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.highPI.cashflow) }}>
              {formatCurrency(analysis.highPI.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.lowIO.cashflow) }}>
              {formatCurrency(analysis.lowIO.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.highIO.cashflow) }}>
              {formatCurrency(analysis.highIO.cashflow)}
            </td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: "700", color: getCashflowColor(tenantAnalysis.highIO.cashflow) }}>{formatCurrency(tenantAnalysis.highIO.cashflow)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Cap Rate (%)</td>
            <td style={tdStyle}>{formatPercent(analysis.lowPI.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.highPI.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.lowIO.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.highIO.capRate)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: 700, color: "#c62828" }}>{formatPercent(tenantAnalysis.highIO.capRate)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>IRR - Year 7</td>
            <td style={tdStyle}>-</td>
            <td style={tdStyle}>-</td>
            <td style={{ ...tdStyle, fontWeight: "700", color: "#28a745" }}>
              {formatPercent(analysis.lowIO.irrYear7)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: "#28a745" }}>
              {formatPercent(analysis.highIO.irrYear7)}
            </td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: "700", color: "#28a745" }}>{formatPercent(tenantAnalysis.highIO.irrYear7)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Break-Even Down Payment</td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.lowPI.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.highPI.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.lowIO.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.highIO.breakEvenDownPayment, propertyPrice)}
            </td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: "600", color: "#0066cc" }}>{formatBreakEvenDownPayment(tenantAnalysis.highIO.breakEvenDownPayment, propertyPrice)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
